// Invoicing mock data
export interface Invoice {
  id: string;
  invoiceNumber: string;
  clientId: string;
  clientName: string;
  clientEmail: string;
  clientAddress: string;
  items: InvoiceItem[];
  subtotal: number;
  taxRate: number;
  tax: number;
  discount?: number;
  discountType?: 'percentage' | 'fixed';
  total: number;
  status: 'draft' | 'sent' | 'viewed' | 'paid' | 'overdue' | 'cancelled';
  issueDate: Date;
  dueDate: Date;
  paidDate?: Date;
  notes?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface InvoiceItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  total: number;
}

export interface Payment {
  id: string;
  invoiceId: string;
  invoiceNumber: string;
  clientName: string;
  amount: number;
  method: 'credit_card' | 'bank_transfer' | 'paypal' | 'check' | 'cash';
  status: 'completed' | 'pending' | 'failed' | 'refunded';
  transactionId?: string;
  processedAt: Date;
  createdAt: Date;
}

export interface InvoiceClient {
  id: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  totalInvoices: number;
  totalPaid: number;
  outstandingBalance: number;
}

export const mockInvoices: Invoice[] = [
  {
    id: 'inv-1',
    invoiceNumber: 'INV-2024-001',
    clientId: 'client-1',
    clientName: 'TechCorp Inc.',
    clientEmail: 'billing@techcorp.com',
    clientAddress: '123 Tech Street, San Francisco, CA 94102',
    items: [
      { id: 'item-1', description: 'Enterprise Software License (Annual)', quantity: 1, unitPrice: 50000, total: 50000 },
      { id: 'item-2', description: 'Implementation Services', quantity: 40, unitPrice: 150, total: 6000 },
      { id: 'item-3', description: 'Training Sessions', quantity: 8, unitPrice: 500, total: 4000 },
    ],
    subtotal: 60000,
    taxRate: 8.5,
    tax: 5100,
    total: 65100,
    status: 'paid',
    issueDate: new Date('2024-01-05'),
    dueDate: new Date('2024-02-04'),
    paidDate: new Date('2024-01-20'),
    notes: 'Thank you for your business!',
    createdAt: new Date('2024-01-05'),
    updatedAt: new Date('2024-01-20'),
  },
  {
    id: 'inv-2',
    invoiceNumber: 'INV-2024-002',
    clientId: 'client-2',
    clientName: 'Global Solutions Ltd.',
    clientEmail: 'ap@globalsolutions.com',
    clientAddress: '456 Business Ave, New York, NY 10001',
    items: [
      { id: 'item-4', description: 'Consulting Services', quantity: 80, unitPrice: 175, total: 14000 },
      { id: 'item-5', description: 'Custom Development', quantity: 120, unitPrice: 150, total: 18000 },
    ],
    subtotal: 32000,
    taxRate: 8.5,
    tax: 2720,
    discount: 3200,
    discountType: 'fixed',
    total: 31520,
    status: 'sent',
    issueDate: new Date('2024-01-15'),
    dueDate: new Date('2024-02-14'),
    notes: '10% discount applied for early commitment',
    createdAt: new Date('2024-01-15'),
    updatedAt: new Date('2024-01-15'),
  },
  {
    id: 'inv-3',
    invoiceNumber: 'INV-2024-003',
    clientId: 'client-3',
    clientName: 'StartupX',
    clientEmail: 'finance@startupx.io',
    clientAddress: '789 Innovation Blvd, Austin, TX 78701',
    items: [
      { id: 'item-6', description: 'Mobile App Development - Phase 1', quantity: 1, unitPrice: 25000, total: 25000 },
    ],
    subtotal: 25000,
    taxRate: 8.25,
    tax: 2062.50,
    total: 27062.50,
    status: 'overdue',
    issueDate: new Date('2023-12-15'),
    dueDate: new Date('2024-01-14'),
    createdAt: new Date('2023-12-15'),
    updatedAt: new Date('2024-01-15'),
  },
  {
    id: 'inv-4',
    invoiceNumber: 'INV-2024-004',
    clientId: 'client-4',
    clientName: 'DataFlow Systems',
    clientEmail: 'accounts@dataflow.com',
    clientAddress: '321 Data Drive, Seattle, WA 98101',
    items: [
      { id: 'item-7', description: 'Cloud Infrastructure Setup', quantity: 1, unitPrice: 15000, total: 15000 },
      { id: 'item-8', description: 'Monthly Maintenance (3 months)', quantity: 3, unitPrice: 2500, total: 7500 },
    ],
    subtotal: 22500,
    taxRate: 10.25,
    tax: 2306.25,
    total: 24806.25,
    status: 'viewed',
    issueDate: new Date('2024-01-18'),
    dueDate: new Date('2024-02-17'),
    createdAt: new Date('2024-01-18'),
    updatedAt: new Date('2024-01-19'),
  },
  {
    id: 'inv-5',
    invoiceNumber: 'INV-2024-005',
    clientId: 'client-5',
    clientName: 'SecureNet Corp',
    clientEmail: 'billing@securenet.com',
    clientAddress: '555 Security Lane, Boston, MA 02101',
    items: [
      { id: 'item-9', description: 'Security Audit - Comprehensive', quantity: 1, unitPrice: 12000, total: 12000 },
      { id: 'item-10', description: 'Penetration Testing', quantity: 1, unitPrice: 8000, total: 8000 },
      { id: 'item-11', description: 'Security Report & Recommendations', quantity: 1, unitPrice: 5000, total: 5000 },
    ],
    subtotal: 25000,
    taxRate: 6.25,
    tax: 1562.50,
    total: 26562.50,
    status: 'paid',
    issueDate: new Date('2024-01-10'),
    dueDate: new Date('2024-02-09'),
    paidDate: new Date('2024-01-18'),
    createdAt: new Date('2024-01-10'),
    updatedAt: new Date('2024-01-18'),
  },
  {
    id: 'inv-6',
    invoiceNumber: 'INV-2024-006',
    clientId: 'client-1',
    clientName: 'TechCorp Inc.',
    clientEmail: 'billing@techcorp.com',
    clientAddress: '123 Tech Street, San Francisco, CA 94102',
    items: [
      { id: 'item-12', description: 'Support Contract (Q1 2024)', quantity: 1, unitPrice: 15000, total: 15000 },
    ],
    subtotal: 15000,
    taxRate: 8.5,
    tax: 1275,
    total: 16275,
    status: 'draft',
    issueDate: new Date('2024-01-20'),
    dueDate: new Date('2024-02-19'),
    createdAt: new Date('2024-01-20'),
    updatedAt: new Date('2024-01-20'),
  },
  {
    id: 'inv-7',
    invoiceNumber: 'INV-2024-007',
    clientId: 'client-6',
    clientName: 'RetailMax',
    clientEmail: 'finance@retailmax.com',
    clientAddress: '888 Commerce Way, Chicago, IL 60601',
    items: [
      { id: 'item-13', description: 'E-commerce Platform Setup', quantity: 1, unitPrice: 35000, total: 35000 },
      { id: 'item-14', description: 'Payment Gateway Integration', quantity: 1, unitPrice: 5000, total: 5000 },
    ],
    subtotal: 40000,
    taxRate: 10.25,
    tax: 4100,
    discount: 10,
    discountType: 'percentage',
    total: 39690,
    status: 'cancelled',
    issueDate: new Date('2024-01-08'),
    dueDate: new Date('2024-02-07'),
    notes: 'Client cancelled project',
    createdAt: new Date('2024-01-08'),
    updatedAt: new Date('2024-01-15'),
  },
];

export const mockPayments: Payment[] = [
  {
    id: 'pay-1',
    invoiceId: 'inv-1',
    invoiceNumber: 'INV-2024-001',
    clientName: 'TechCorp Inc.',
    amount: 65100,
    method: 'bank_transfer',
    status: 'completed',
    transactionId: 'TXN-20240120-001',
    processedAt: new Date('2024-01-20T10:30:00'),
    createdAt: new Date('2024-01-20'),
  },
  {
    id: 'pay-2',
    invoiceId: 'inv-5',
    invoiceNumber: 'INV-2024-005',
    clientName: 'SecureNet Corp',
    amount: 26562.50,
    method: 'credit_card',
    status: 'completed',
    transactionId: 'TXN-20240118-001',
    processedAt: new Date('2024-01-18T14:15:00'),
    createdAt: new Date('2024-01-18'),
  },
  {
    id: 'pay-3',
    invoiceId: 'inv-3',
    invoiceNumber: 'INV-2024-003',
    clientName: 'StartupX',
    amount: 10000,
    method: 'bank_transfer',
    status: 'pending',
    processedAt: new Date('2024-01-21T09:00:00'),
    createdAt: new Date('2024-01-21'),
  },
  {
    id: 'pay-4',
    invoiceId: 'inv-2',
    invoiceNumber: 'INV-2024-002',
    clientName: 'Global Solutions Ltd.',
    amount: 31520,
    method: 'paypal',
    status: 'pending',
    processedAt: new Date('2024-01-21T11:30:00'),
    createdAt: new Date('2024-01-21'),
  },
  {
    id: 'pay-5',
    invoiceId: 'inv-7',
    invoiceNumber: 'INV-2024-007',
    clientName: 'RetailMax',
    amount: 39690,
    method: 'credit_card',
    status: 'refunded',
    transactionId: 'TXN-20240115-001',
    processedAt: new Date('2024-01-15T16:45:00'),
    createdAt: new Date('2024-01-15'),
  },
];

export const mockInvoiceClients: InvoiceClient[] = [
  {
    id: 'client-1',
    name: 'TechCorp Inc.',
    email: 'billing@techcorp.com',
    phone: '+1 (555) 123-4567',
    address: '123 Tech Street, San Francisco, CA 94102',
    totalInvoices: 5,
    totalPaid: 165000,
    outstandingBalance: 16275,
  },
  {
    id: 'client-2',
    name: 'Global Solutions Ltd.',
    email: 'ap@globalsolutions.com',
    phone: '+1 (555) 234-5678',
    address: '456 Business Ave, New York, NY 10001',
    totalInvoices: 3,
    totalPaid: 75000,
    outstandingBalance: 31520,
  },
  {
    id: 'client-3',
    name: 'StartupX',
    email: 'finance@startupx.io',
    phone: '+1 (555) 345-6789',
    address: '789 Innovation Blvd, Austin, TX 78701',
    totalInvoices: 2,
    totalPaid: 0,
    outstandingBalance: 27062.50,
  },
  {
    id: 'client-4',
    name: 'DataFlow Systems',
    email: 'accounts@dataflow.com',
    phone: '+1 (555) 456-7890',
    address: '321 Data Drive, Seattle, WA 98101',
    totalInvoices: 1,
    totalPaid: 0,
    outstandingBalance: 24806.25,
  },
  {
    id: 'client-5',
    name: 'SecureNet Corp',
    email: 'billing@securenet.com',
    phone: '+1 (555) 567-8901',
    address: '555 Security Lane, Boston, MA 02101',
    totalInvoices: 4,
    totalPaid: 85000,
    outstandingBalance: 0,
  },
];

// Invoicing stats
export const invoicingStats = {
  totalRevenue: 191506.25,
  revenueChange: 15.3,
  paidInvoices: 2,
  paidAmount: 91662.50,
  pendingInvoices: 3,
  pendingAmount: 83388.75,
  overdueInvoices: 1,
  overdueAmount: 27062.50,
  draftInvoices: 1,
  draftAmount: 16275,
};

// Revenue by month
export const invoicingRevenueByMonth = [
  { month: 'Aug', revenue: 45000 },
  { month: 'Sep', revenue: 52000 },
  { month: 'Oct', revenue: 48000 },
  { month: 'Nov', revenue: 61000 },
  { month: 'Dec', revenue: 75000 },
  { month: 'Jan', revenue: 91662.50 },
];

// Alias for backward compatibility
export const revenueByMonth = invoicingRevenueByMonth;

// Invoice status breakdown
export const invoicesByStatus = [
  { status: 'Paid', count: 2, amount: 91662.50, color: 'green' },
  { status: 'Sent', count: 1, amount: 31520, color: 'blue' },
  { status: 'Viewed', count: 1, amount: 24806.25, color: 'cyan' },
  { status: 'Overdue', count: 1, amount: 27062.50, color: 'red' },
  { status: 'Draft', count: 1, amount: 16275, color: 'gray' },
  { status: 'Cancelled', count: 1, amount: 39690, color: 'orange' },
];

// Payment methods breakdown
export const paymentsByMethod = [
  { method: 'Bank Transfer', count: 2, amount: 75100, color: 'blue' },
  { method: 'Credit Card', count: 2, amount: 66252.50, color: 'green' },
  { method: 'PayPal', count: 1, amount: 31520, color: 'cyan' },
  { method: 'Check', count: 0, amount: 0, color: 'orange' },
];
